import React, { useState, useEffect } from 'react';
import { useEnquiry } from '../../contexts/EnquiryContext';
import { 
  AlertTriangle, 
  CheckCircle, 
  Database, 
  RefreshCw, 
  Trash2, 
  Plus,
  Download,
  Upload,
  Code
} from 'lucide-react';
import LocalStorageDebugger from '../../utils/LocalStorageDebugger';

const EnquiryDebugger = () => {
  const { enquiries, addEnquiry } = useEnquiry();
  const [diagnosticResults, setDiagnosticResults] = useState(null);
  const [localStorageContent, setLocalStorageContent] = useState('');
  const [importData, setImportData] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    runDiagnostic();
    loadLocalStorageContent();
  }, []);

  const runDiagnostic = () => {
    const results = LocalStorageDebugger.runDiagnostic();
    setDiagnosticResults(results);
    
    if (results.errors.length > 0) {
      setMessage({
        type: 'error',
        text: `Diagnostic found ${results.errors.length} issues. See details below.`
      });
    } else {
      setMessage({
        type: 'success',
        text: 'Diagnostic completed successfully. No issues found.'
      });
    }
  };

  const loadLocalStorageContent = () => {
    try {
      const content = localStorage.getItem('contact_enquiries') || '[]';
      setLocalStorageContent(JSON.stringify(JSON.parse(content), null, 2));
    } catch (e) {
      setLocalStorageContent(`Error: ${e.message}`);
    }
  };

  const fixLocalStorage = () => {
    const result = LocalStorageDebugger.fixLocalStorage();
    setMessage({
      type: result.success ? 'success' : 'error',
      text: result.message
    });
    loadLocalStorageContent();
    runDiagnostic();
  };

  const addTestEnquiry = () => {
    try {
      const testEnquiry = LocalStorageDebugger.addTestEnquiry();
      if (testEnquiry) {
        setMessage({
          type: 'success',
          text: 'Test enquiry added successfully'
        });
        loadLocalStorageContent();
        runDiagnostic();
      }
    } catch (e) {
      setMessage({
        type: 'error',
        text: `Error adding test enquiry: ${e.message}`
      });
    }
  };

  const clearEnquiries = () => {
    if (window.confirm('Are you sure you want to clear all enquiries? This cannot be undone.')) {
      try {
        LocalStorageDebugger.clearEnquiries();
        setMessage({
          type: 'success',
          text: 'All enquiries cleared successfully'
        });
        loadLocalStorageContent();
        runDiagnostic();
      } catch (e) {
        setMessage({
          type: 'error',
          text: `Error clearing enquiries: ${e.message}`
        });
      }
    }
  };

  const saveLocalStorageChanges = () => {
    try {
      const parsed = JSON.parse(localStorageContent);
      localStorage.setItem('contact_enquiries', JSON.stringify(parsed));
      setMessage({
        type: 'success',
        text: 'Changes saved successfully'
      });
      runDiagnostic();
    } catch (e) {
      setMessage({
        type: 'error',
        text: `Error saving changes: ${e.message}`
      });
    }
  };

  const exportEnquiries = () => {
    try {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(localStorageContent);
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", "enquiries_export.json");
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      
      setMessage({
        type: 'success',
        text: 'Enquiries exported successfully'
      });
    } catch (e) {
      setMessage({
        type: 'error',
        text: `Error exporting enquiries: ${e.message}`
      });
    }
  };

  const importEnquiries = () => {
    try {
      const parsed = JSON.parse(importData);
      if (!Array.isArray(parsed)) {
        throw new Error('Import data must be an array');
      }
      
      localStorage.setItem('contact_enquiries', JSON.stringify(parsed));
      setMessage({
        type: 'success',
        text: `Imported ${parsed.length} enquiries successfully`
      });
      loadLocalStorageContent();
      runDiagnostic();
    } catch (e) {
      setMessage({
        type: 'error',
        text: `Error importing enquiries: ${e.message}`
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">Enquiry System Debugger</h1>
          <p className="text-gray-600">Troubleshoot and fix issues with the contact enquiry system</p>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' 
              ? 'bg-red-50 border border-red-200 text-red-700' 
              : 'bg-green-50 border border-green-200 text-green-700'
          }`}>
            {message.type === 'error' ? <AlertTriangle className="inline mr-2 h-5 w-5" /> : <CheckCircle className="inline mr-2 h-5 w-5" />}
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Actions */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-primary mb-4">Debug Actions</h2>
              
              <div className="space-y-4">
                <button 
                  onClick={runDiagnostic}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Run Diagnostic
                </button>
                
                <button 
                  onClick={fixLocalStorage}
                  className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <CheckCircle className="w-5 h-5" />
                  Fix LocalStorage
                </button>
                
                <button 
                  onClick={addTestEnquiry}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add Test Enquiry
                </button>
                
                <button 
                  onClick={clearEnquiries}
                  className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-5 h-5" />
                  Clear All Enquiries
                </button>
                
                <button 
                  onClick={exportEnquiries}
                  className="w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Export Enquiries
                </button>
              </div>
            </div>
          </div>

          {/* Diagnostic Results */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-bold text-primary mb-4">Diagnostic Results</h2>
              
              {diagnosticResults ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">LocalStorage Available</p>
                      <p className={`font-semibold ${diagnosticResults.localStorageAvailable ? 'text-green-600' : 'text-red-600'}`}>
                        {diagnosticResults.localStorageAvailable ? 'Yes' : 'No'}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Enquiries Exist</p>
                      <p className={`font-semibold ${diagnosticResults.enquiriesExist ? 'text-green-600' : 'text-red-600'}`}>
                        {diagnosticResults.enquiriesExist ? 'Yes' : 'No'}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Enquiries Valid</p>
                      <p className={`font-semibold ${diagnosticResults.enquiriesValid ? 'text-green-600' : 'text-red-600'}`}>
                        {diagnosticResults.enquiriesValid ? 'Yes' : 'No'}
                      </p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Total Enquiries</p>
                      <p className="font-semibold text-blue-600">{diagnosticResults.enquiriesCount}</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">New Enquiries</p>
                      <p className="font-semibold text-green-600">{diagnosticResults.newEnquiriesCount}</p>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Latest Enquiry</p>
                      <p className="font-semibold text-gray-800">
                        {diagnosticResults.newestEnquiryDate 
                          ? new Date(diagnosticResults.newestEnquiryDate).toLocaleString() 
                          : 'None'}
                      </p>
                    </div>
                  </div>
                  
                  {diagnosticResults.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h3 className="font-semibold text-red-700 mb-2">Errors Found ({diagnosticResults.errors.length})</h3>
                      <ul className="list-disc pl-5 text-red-600">
                        {diagnosticResults.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Run diagnostic to see results</p>
              )}
            </div>
          </div>
        </div>

        {/* LocalStorage Editor */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-bold text-primary mb-4">LocalStorage Editor</h2>
          
          <div className="mb-4">
            <textarea
              value={localStorageContent}
              onChange={(e) => setLocalStorageContent(e.target.value)}
              className="w-full h-64 font-mono text-sm p-4 border border-gray-300 rounded-lg"
            />
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={saveLocalStorageChanges}
              className="bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Database className="w-5 h-5" />
              Save Changes
            </button>
          </div>
        </div>

        {/* Import Enquiries */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-primary mb-4">Import Enquiries</h2>
          
          <div className="mb-4">
            <textarea
              value={importData}
              onChange={(e) => setImportData(e.target.value)}
              placeholder="Paste JSON array of enquiries here..."
              className="w-full h-32 font-mono text-sm p-4 border border-gray-300 rounded-lg"
            />
          </div>
          
          <div className="flex justify-end">
            <button
              onClick={importEnquiries}
              className="bg-primary hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <Upload className="w-5 h-5" />
              Import Enquiries
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryDebugger;
